/**
 * McpConnectionsPanel — Phase 1 of the MCP layer.
 * Lets a user mint/revoke Personal Access Tokens so an MCP client
 * (Claude, ChatGPT, Codex…) can read their Studio Media Labs data.
 * Only rendered when REACT_APP_MCP_ENABLED === 'true'.
 */
import { memo, useCallback, useEffect, useState } from 'react';
import {
  X, Plug, Plus, Trash2, Copy, Check, Loader2, KeyRound, ShieldAlert, Database,
  SlidersHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { mcpService } from '../../services/mcpService';
import McpLimitsBox from './McpLimitsBox';
import McpTokenScopes from './McpTokenScopes';

const SCOPE_LABELS = {
  'projects:read': 'Leer proyectos',
  'projects:write': 'Crear/editar proyectos',
  'assets:read': 'Leer assets',
  'assets:write': 'Subir assets',
  'workflows:read': 'Leer workflows',
  'workflows:write': 'Editar workflows',
  'workflows:execute': 'Ejecutar (gasta créditos)',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es', {
  day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

const tokenState = (t) => {
  if (t.revoked_at) return { label: 'Revocado', cls: 'revoked' };
  if (t.expires_at && new Date(t.expires_at) < new Date()) {
    return { label: 'Expirado', cls: 'expired' };
  }
  return { label: 'Activo', cls: 'active' };
};

const McpConnectionsPanel = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState([]);
  const [freshToken, setFreshToken] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scopeCatalog, setScopeCatalog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mcpService.listTokens();
      setData(res);
      if (!scopes.length && res.default_scopes) setScopes(res.default_scopes);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las conexiones MCP');
    } finally {
      setLoading(false);
    }
  }, [scopes.length]);

  useEffect(() => { load(); }, []);
  useEffect(() => {
    mcpService.listScopes().then(setScopeCatalog).catch(() => setScopeCatalog(null));
  }, []);

  const toggleScope = (s) =>
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const create = async () => {
    if (!name.trim()) return toast.error('Ponele un nombre a la conexión');
    setCreating(true);
    try {
      const res = await mcpService.createToken({ name: name.trim(), scopes });
      setFreshToken(res.token);
      setName('');
      toast.success('Token creado — copialo ahora, no se vuelve a mostrar');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear el token');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await mcpService.revokeToken(id);
      toast.success('Token revocado — el acceso se cortó de inmediato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo revocar');
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(freshToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="credits-panel-overlay" onClick={onClose} data-testid="mcp-panel-overlay">
      <div className="credits-panel mcp-panel" onClick={(e) => e.stopPropagation()} data-testid="mcp-panel">
        <div className="credits-panel-header">
          <div className="credits-panel-title">
            <Plug size={18} />
            <span>Conexiones MCP</span>
          </div>
          <button className="credits-panel-close" onClick={onClose} data-testid="mcp-panel-close">
            <X size={16} />
          </button>
        </div>

        <div className="mcp-panel-body">
          {loading && !data ? (
            <div className="mcp-loading" data-testid="mcp-loading">
              <Loader2 size={18} className="animate-spin" /> Cargando…
            </div>
          ) : (
            <>
              {data?.table_missing && (
                <div className="mcp-warning" data-testid="mcp-table-missing">
                  <Database size={14} />
                  <span>
                    Falta la tabla de tokens. Corré <code>{data.migration}</code> en Supabase.
                  </span>
                </div>
              )}

              {data && data.user_enabled === false && (
                <div className="mcp-warning" data-testid="mcp-user-disabled">
                  <ShieldAlert size={14} />
                  <span>
                    Tu usuario todavía no tiene habilitado el acceso MCP. Un administrador
                    lo activa desde <em>Administración → Seguridad y Status → Acceso MCP</em>.
                  </span>
                </div>
              )}

              <p className="mcp-endpoint" data-testid="mcp-endpoint">
                Endpoint MCP: <code>{data?.endpoint}</code>
                <em>Autenticación: header <code>Authorization: Bearer &lt;token&gt;</code></em>
              </p>

              {freshToken && (
                <div className="mcp-fresh-token" data-testid="mcp-fresh-token">
                  <div className="mcp-fresh-head">
                    <KeyRound size={14} />
                    <strong>Copialo ahora: el secreto se muestra una sola vez</strong>
                    <button
                      className="mcp-fresh-dismiss"
                      onClick={() => setFreshToken(null)}
                      title="Ya lo copié"
                      data-testid="mcp-dismiss-token"
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="mcp-fresh-row">
                    <code data-testid="mcp-fresh-token-value">{freshToken}</code>
                    <button onClick={copy} data-testid="mcp-copy-token">
                      {copied ? <Check size={13} /> : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              )}

              <McpLimitsBox />

              <div className="mcp-create">
                <input
                  type="text"
                  placeholder="Nombre de la conexión (ej. Claude Desktop)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="mcp-token-name"
                />
                <div className="mcp-scopes">
                  {(data?.all_scopes || []).map((s) => (
                    <label key={s} className={scopes.includes(s) ? 'on' : ''} data-testid={`mcp-scope-${s}`}>
                      <input
                        type="checkbox"
                        checked={scopes.includes(s)}
                        onChange={() => toggleScope(s)}
                      />
                      {SCOPE_LABELS[s] || s}
                      {s === 'workflows:execute' && <ShieldAlert size={11} />}
                    </label>
                  ))}
                </div>
                <button
                  className="mcp-create-btn"
                  onClick={create}
                  disabled={creating || data?.table_missing}
                  data-testid="mcp-create-token"
                >
                  {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  Crear token
                </button>
              </div>

              <div className="mcp-token-list" data-testid="mcp-token-list">
                {(data?.tokens || []).length === 0 ? (
                  <p className="mcp-empty" data-testid="mcp-tokens-empty">Todavía no hay conexiones.</p>
                ) : data.tokens.map((t) => {
                  const st = tokenState(t);
                  return (
                    <div
                      className={`mcp-token ${st.cls}`}
                      key={t.id}
                      data-testid={`mcp-token-${t.id}`}
                    >
                      <div className="mcp-token-main">
                        <strong>{t.name}</strong>
                        <code>{t.token_prefix}…</code>
                        <span
                          className={`mcp-state-tag ${st.cls}`}
                          data-testid={`mcp-token-state-${t.id}`}
                        >
                          {st.label}
                        </span>
                      </div>
                      <div className="mcp-token-scopes" data-testid={`mcp-token-scopes-${t.id}`}>
                        {(t.scopes || []).map((s) => (
                          <span key={s} className={`mcp-scope-chip ${s === 'workflows:execute' ? 'danger' : ''}`}>
                            {s}
                          </span>
                        ))}
                      </div>
                      <div className="mcp-token-meta">
                        <span>creado {fmt(t.created_at)}</span>
                        <span>último uso {fmt(t.last_used_at)}</span>
                        <span>
                          {t.revoked_at
                            ? `revocado ${fmt(t.revoked_at)}`
                            : `vence ${t.expires_at ? fmt(t.expires_at) : 'nunca'}`}
                        </span>
                      </div>
                      {!t.revoked_at && (
                        <div className="mcp-token-actions">
                          <button
                            className="mcp-edit-scopes"
                            onClick={() => setEditing(editing === t.id ? null : t.id)}
                            data-testid={`mcp-edit-scopes-${t.id}`}
                          >
                            <SlidersHorizontal size={12} /> Editar permisos
                          </button>
                          <button
                            className="mcp-revoke"
                            onClick={() => revoke(t.id)}
                            data-testid={`mcp-revoke-${t.id}`}
                          >
                            <Trash2 size={12} /> Revocar
                          </button>
                        </div>
                      )}
                      {editing === t.id && (
                        <McpTokenScopes
                          token={t}
                          catalog={scopeCatalog}
                          onClose={() => setEditing(null)}
                          onSaved={load}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(McpConnectionsPanel);
